<template>
    <div class="min-w-[1024px]">
        <div class="flex justify-between items-start">
            <div>
                <h3 class="font-inter-heading-10">Wallet ({{ walletCategoryList.length }})</h3>
                <p class="font-inter-body-3 text-gray-600">Each wallet allows category management, and a maximum of 5 wallets can be added.</p>
            </div>
            <div 
                class="flex items-center gap-2.5 mt-1"
            >
                <div class="relative">
                    <ElementsTooltipSingleton :ref="setTooltipRef" />
                    <button
                        :ref="attachReorderTooltip"
                        @click="onClickReorder"
                        class="w-[46px] h-[46px] rounded-[10px] border border-gray-07 bg-background-basic-1 flex items-center justify-center hover:bg-gray-100 shrink-0"
                    >
                        <ReorderIcon :width="26" :height="26" color="text-gray-500" />
                    </button>
                </div>
                <DesignButton
                    btnText="View History"
                    btnSize="MD"
                    btnType="QUATERNARY"
                    :btnDisabled="false"
                    @click-event="showHistoryModal"
                />
                <DesignButton 
                    btnSize="MD"
                    btnType="PRIMARY"
                    :btnDisabled="false"
                    btnText="Add Wallet"
                    @click-event="onClickAddWallet"
                >
                    <template #front-icon>
                        <AddIcon :width="16" :height="16" color="text-white" weight="FILL" />
                    </template>
                </DesignButton>
            </div>
        </div>

        <div class="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div v-for="walletCategory in walletCategoryList" :key="walletCategory.walletCategorySeq" class="bg-background-basic-1 border border-basic-2 rounded-02 pl-6 pr-5 py-5 h-[200px] flex flex-col justify-between shrink-0">
                <div class="flex justify-between items-start">
                    <div class="font-inter-heading-13 text-content-basic-1 flex-1 min-w-0 pr-2 truncate">{{ walletCategory.walletCategoryName ?? walletCategory.walletCategoryName }}</div>
                    <Menu as="div" class="relative inline-block text-left">
                        <div>
                            <MenuButton class="inline-flex justify-center rounded-md p-2 hover:bg-gray-50">
                                <MoreHorizIcon :width="24" :height="24" color="text-content-basic-2" weight="REGULAR" />
                            </MenuButton>
                        </div>
                        <transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95">
                            <MenuItems class="absolute right-0 mt-2 min-w-[104px] max-h-[400px] overflow-auto origin-top-right bg-background-basic-1 border border-border-basic-2 rounded-02 shadow-dropdown p-2 focus:outline-none">
                                <div class="flex flex-col gap-1">
                                    <MenuItem v-slot="{ active }">
                                        <button @click="onEditWallet(walletCategory.walletCategorySeq)" 
                                                :class="['w-full text-left px-3 py-2 rounded-01_5 font-inter-body-2', active ? 'bg-background-basic-2 text-content-basic-2' : 'text-content-basic-2']">
                                            Edit
                                        </button>
                                    </MenuItem>
                                    <MenuItem v-slot="{ active }">
                                        <button @click="onDeleteWallet(walletCategory.walletCategorySeq)" 
                                                :class="['w-full text-left px-3 py-2 rounded-01_5 font-inter-body-2', active ? 'bg-background-basic-2 text-content-basic-2' : 'text-content-basic-2']">
                                            Delete
                                        </button>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
                <p class="mt-3 font-inter-body-3 leading-[1.5] text-gray-600 line-clamp-2 break-words min-h-[42px]">{{ walletCategory.description ?? walletCategory.description }}</p>
                <div class="mt-8 flex items-center gap-2 text-content-basic-2">
                    <HomeStorage :width="24" :height="24" color="text-content-basic-2" />
                    <span class="font-inter-heading-20 text-content-basic-2">{{ (walletCategory.allowFlexbenCount ?? walletCategory.allowFlexbenCount ?? 0) }} Category</span>
                </div>
            </div>
        </div>
        
        <Teleport to="body">
            <DesignReorderModal
                v-model="showReorderModal"
                :items="walletCategoryList"
                itemKey="walletCategorySeq"
                labelKey="walletCategoryName"
                title="Reorder Wallet"
                @save="reorderWalletCategoryList"
            />

            <DesignViewHistoryModal
                v-model="visibleHistoryModal"
                :featureName="walletCategoryFeatureName"
                :contentsPerPage="10"
                :pageSize="9"
            />

            <DeletePopup
                v-model="showDeletePopup"
                title="Delete Confirmation"
                content="Deleting this wallet will return all Sub-admin points to the Admin account. (Points already distributed to employees will not be returned.)"
                checkboxText="Yes, I confirm to delete this wallet."
                @button-event="deleteWalletCategory(deleteWalletCategorySeq)"
            />

            <DesignConfirmPopup
                v-model="showConfirmPopup"
                :showTitle="confirmTitle !== ''"
                :title="confirmTitle"
                :confirmDescription="confirmMessage"
                buttonText="OK"
            />
        </Teleport>
    </div>
</template>

<style scoped>
.blu {
    background: #4361ee;
}
.blu:hover {
    background: #334cba;
}
</style>

<script>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import HomeStorage from '@/components/design-system/icon/home-storage.vue';
import ReorderIcon from '@/components/design-system/icon/reorder.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import AddIcon from '@/components/design-system/icon/add.vue';
import DesignReorderModal from '@/components/design-system/design-reorder-modal.vue';
import DeletePopup from '@/components/DeletePopup.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import ElementsTooltipSingleton from '@/components/elements/ElementsTooltipSingleton.vue';
import MoreHorizIcon from '@/components/design-system/icon/more-horiz.vue';
import { featureName } from '@/assets/data/optionData';
import DesignViewHistoryModal from '@/components/design-system/design-view-history-modal.vue';

export default {
    components: {
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        HomeStorage,
        ReorderIcon,
        DesignButton,
        AddIcon,
        DesignReorderModal,
        DeletePopup,
        DesignConfirmPopup,
        ElementsTooltipSingleton,
        MoreHorizIcon,
        DesignViewHistoryModal,
    },
    data() {
        return {
            walletCategoryList: [],
            deleteWalletCategorySeq: null,
            showReorderModal: false,
            showDeletePopup: false,
            showConfirmPopup: false,
            confirmTitle: '',
            confirmMessage: '',
            visibleHistoryModal: false,
            scrollYBackup: 0,
        }; 
    },
    mounted() {
        const self = this;
        self.getWalletCategoryList();
    },
    computed: {
        walletCategoryFeatureName() {
            return featureName.WALLET_CATEGORY;
        },
        // 여기에 모달이 더 있으면 || 로 추가
        isAnyPopupOpen() {
            return (
                this.showReorderModal ||
                this.showDeletePopup ||
                this.showConfirmPopup ||
                this.visibleHistoryModal
            );
        },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    methods: {
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        setTooltipRef(el) {
            this.$tooltip = el;
        },
        attachReorderTooltip(el) {
            if (!el) return;
            // ensure tooltip instance exists
            if (this.$tooltip && this.$tooltip.loadBasic) {
                this.$tooltip.loadBasic(el, () => 'Reorder', { placement: 'top' });
            }
        },
        onClickAddWallet() {
            if (this.walletCategoryList.length >= 5) {
                this.setConfirmPopup('Up to 5 wallets can be created.');
                return;
            }

            const baseUrl = '/flex_ben/flex_ben_setting/wallet/add_wallet_category';
            window.location.href = baseUrl;
        },
        onClickReorder() {
            this.showReorderModal = true;
        },
        onEditWallet(walletCategorySeq) {
            const baseUrl = '/flex_ben/flex_ben_setting/wallet/edit_wallet_category';
            const paramKey = 'walletCategorySeq';
            window.location.href = baseUrl + `?${encodeURIComponent(paramKey)}=${encodeURIComponent(walletCategorySeq)}`;
        },
        onDeleteWallet(walletCategorySeq) {
            if (this.walletCategoryList.length <= 1) {
                this.setConfirmPopup('At least one wallet must be retained.');
                return;
            }

            this.deleteWalletCategorySeq = walletCategorySeq;
            this.showDeletePopup = true;
        },
        getWalletCategoryList() {
            const self = this;
            const url = self.$api('uri', 'get-wallet-category-list');
            self.$axios
                .get(url)
                .then((res) => {
                    self.walletCategoryList = res.data?.data?.list ?? [];
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        setConfirmPopup(message, title = '') {
            this.showConfirmPopup = true;
            this.confirmTitle = title;
            this.confirmMessage = message;
        },
        deleteWalletCategory(walletCategorySeq) {
            const url = this.$api('uri', 'delete-wallet-category').replace('{walletCategorySeq}', walletCategorySeq);
            this.$axios.delete(url)
                .then(() => {
                    this.showDeletePopup = false;
                    this.setConfirmPopup('The wallet has been deleted successfully.', 'Delete Completed');
                    this.getWalletCategoryList();
                })
                .catch((err) => {
                    this.showDeletePopup = false;
                    if (err.response.data.code === 'HR_WALLET_CATEGORY_DELETE_MIN_REQUIRED') {
                        this.setConfirmPopup('At least one wallet must be retained.');
                        this.getWalletCategoryList();
                        return;
                    }

                    this.setConfirmPopup('Failed to delete wallet category. Please try again.');
                });
        },
        reorderWalletCategoryList(payload) {
            const url = this.$api('uri', 'put-wallet-category-order');
            this.$axios.put(url, payload)
                .then(() => {
                    this.getWalletCategoryList();
                })
                .catch((err) => {
                    this.setConfirmPopup('Failed to save order. Please try again');
                });
        },
        showHistoryModal() {
            this.visibleHistoryModal = true;
        },
    },
};
</script>




